import { useEffect, useState } from "react";
import { getProperty, safeParseJSON } from "../../../utils/jsonValidator";

export const useManageComponentFlow = (flowInEdition, setOpenModal, setValeComponentFlowInEdition) => {
    const [orders, setOrders] = useState({ nextOrder: "01", listOrders: ["01"] });
    const [itemEditComponentFlow, setitemEditComponentFlow] = useState({});
    const [flowComponent, setflowComponent] = useState([]);
    const [typeAction, settypeAction] = useState("");

    useEffect(() => {
        const component = safeParseJSON(flowInEdition?.component);
        setflowComponent(getProperty(component, "flow_component") || []);
    }, [flowInEdition]);

    //#region HELPERS
    const splitOrder = (order) => order.split(".").map(Number);

    const compareOrders = (a, b, missingAsZero = false) => {
        const aParts = splitOrder(a);
        const bParts = splitOrder(b);
        const len = Math.max(aParts.length, bParts.length);
        for (let i = 0; i < len; i++) {
            const aVal = aParts[i] ?? (missingAsZero ? 0 : -1);
            const bVal = bParts[i] ?? (missingAsZero ? 0 : -1);
            if (aVal !== bVal) return aVal - bVal;
        }
        return 0;
    };

    const getNextOrder = (data, baseOrder = "") => {
        // Root virtual node "00" means top-level siblings (01, 02, …)
        const normalizedBase = !baseOrder || baseOrder === "00" ? "" : baseOrder;
        const prefix = normalizedBase ? `${normalizedBase}.` : "";
        const baseLevel = normalizedBase ? normalizedBase.split(".").length + 1 : 1;

        const siblings = data
            .map((i) => i.order)
            .filter((o) => o.startsWith(prefix) && o.split(".").length === baseLevel)
            .sort((a, b) => compareOrders(a, b));

        const lastNum = siblings.length
            ? parseInt(siblings[siblings.length - 1].split(".").pop(), 10)
            : 0;

        const next = (lastNum + 1).toString().padStart(2, "0");
        return {
            nextOrder: prefix + next,
            listOrders: [...siblings, prefix + next],
        };
    };

    const incrementOrder = (order, baseParts) => {
        const parts = order.split(".");
        const level = baseParts.length - 1;
        const length = parts[level].length;
        parts[level] = (parseInt(parts[level]) + 1).toString().padStart(length, "0");
        return parts.join(".");
    };

    const isAffected = (currentOrder, baseParts) => {
        const parts = currentOrder.split(".");
        if (parts.length < baseParts.length) return false;
        for (let i = 0; i < baseParts.length - 1; i++) {
            if (baseParts[i] !== parts[i]) return false;
        }
        return parseInt(parts[baseParts.length - 1]) >= parseInt(baseParts[baseParts.length - 1]);
    };

    /** Decrement the segment at removedOrder's level when order is a later sibling/descendant. */
    const decrementOrderAfterRemoval = (order, removedOrder) => {
        const baseParts = removedOrder.split(".");
        const orderParts = order.split(".");
        const level = baseParts.length - 1;
        if (orderParts.length <= level) return order;

        const parentPrefix = baseParts.slice(0, level).join(".");
        if (parentPrefix !== orderParts.slice(0, level).join(".")) return order;

        const removedIdx = parseInt(baseParts[level], 10);
        const orderIdx = parseInt(orderParts[level], 10);
        if (orderIdx > removedIdx) {
            const next = [...orderParts];
            next[level] = (orderIdx - 1).toString().padStart(orderParts[level].length, "0");
            return next.join(".");
        }
        return order;
    };

    const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    /**
     * Remap order references in strings (single hop, no cascade):
     * - exact order (input / replace / insert): "02" → "03"
     * - {[order]} / {[order].field} in body
     * - [order].field insert paths
     * newOrd === null → clear reference to a deleted order
     *
     * Important: map only from the original token. With orderMap
     * {02→03, 03→04}, input "02" must become "03" (not "04").
     */
    const applyOrderMapToValue = (value, orderMap) => {
        if (typeof value === "string") {
            if (!orderMap || Object.keys(orderMap).length === 0) return value;

            // Plain order fields: one lookup from the original value
            if (Object.prototype.hasOwnProperty.call(orderMap, value)) {
                const mapped = orderMap[value];
                return mapped == null ? "" : mapped;
            }

            let newStr = value;

            // Drop references to deleted orders
            Object.entries(orderMap)
                .filter(([, newOrd]) => newOrd == null)
                .sort((a, b) => b[0].length - a[0].length)
                .forEach(([oldOrd]) => {
                    newStr = newStr.replace(
                        new RegExp(`\\{\\[${escapeRegExp(oldOrd)}\\](?:\\.[^}]*)?\\}`, "g"),
                        ""
                    );
                    newStr = newStr.replace(
                        new RegExp(`^\\[${escapeRegExp(oldOrd)}\\](?:\\..*)?$`, "g"),
                        ""
                    );
                });

            // {[ord]} / {[ord].field} — replace ord using the original token only
            newStr = newStr.replace(/\{\[(\d+(?:\.\d+)*)\]/g, (full, ord) => {
                if (
                    Object.prototype.hasOwnProperty.call(orderMap, ord) &&
                    orderMap[ord] != null
                ) {
                    return `{[${orderMap[ord]}]`;
                }
                return full;
            });

            // [ord].field insert paths — avoid matching the "[" inside "{[ord]"
            newStr = newStr.replace(/(^|[^{])\[(\d+(?:\.\d+)*)\]/g, (full, prefix, ord) => {
                if (
                    Object.prototype.hasOwnProperty.call(orderMap, ord) &&
                    orderMap[ord] != null
                ) {
                    return `${prefix}[${orderMap[ord]}]`;
                }
                return full;
            });

            return newStr;
        }
        if (Array.isArray(value)) return value.map((item) => applyOrderMapToValue(item, orderMap));
        if (value && typeof value === "object") {
            const next = {};
            Object.keys(value).forEach((key) => {
                next[key] = applyOrderMapToValue(value[key], orderMap);
            });
            return next;
        }
        return value;
    };

    const applyOrderMapToComponent = (comp, orderMap) => {
        const updatedComp = { ...comp };
        ["input", "replace", "insert"].forEach((field) => {
            if (updatedComp[field] == null || updatedComp[field] === "") return;
            const mapped = applyOrderMapToValue(updatedComp[field], orderMap);
            if (mapped === "" || mapped == null) {
                delete updatedComp[field];
            } else {
                updatedComp[field] = mapped;
            }
        });
        if (updatedComp.body) {
            updatedComp.body = applyOrderMapToValue(updatedComp.body, orderMap);
        }
        return updatedComp;
    };

    // Reorder
    const deleteAndReorder = (components, orderToDelete) => {
        const orderMap = {};

        // Orders removed with the deleted block must not remain as references
        components.forEach((comp) => {
            if (
                comp.order === orderToDelete ||
                comp.order.startsWith(`${orderToDelete}.`)
            ) {
                orderMap[comp.order] = null;
            }
        });

        const filtered = components
            .filter(
                (comp) =>
                    comp.order !== orderToDelete &&
                    !comp.order.startsWith(`${orderToDelete}.`)
            )
            .map((comp) => ({ ...comp, _orig: comp.order }));

        const updated = filtered.map((comp) => {
            const nextOrder = decrementOrderAfterRemoval(comp.order, orderToDelete);
            if (nextOrder !== comp.order) {
                orderMap[comp._orig] = nextOrder;
                return { ...comp, order: nextOrder };
            }
            return comp;
        });

        return updated
            .map(({ _orig, ...comp }) => applyOrderMapToComponent(comp, orderMap))
            .sort((a, b) => compareOrders(a.order, b.order));
    };

    const buildOrderMap = (items) => {
        const orderMap = {};
        items.forEach((comp) => {
            if (comp._orig && comp._orig !== comp.order) {
                orderMap[comp._orig] = comp.order;
            }
        });
        return orderMap;
    };

    /**
     * Insert a component at newComponent.order, shifting siblings/descendants forward,
     * and remap input / replace / insert / {[order]} references on all components
     * (including the new one, in case it points at orders that just shifted).
     */
    const insertAndReorder = (components, newComponent) => {
        const baseParts = newComponent.order.split(".");
        const withOrig = components.map((comp) => ({ ...comp, _orig: comp.order }));
        const shifted = withOrig.map((comp) =>
            isAffected(comp.order, baseParts)
                ? { ...comp, order: incrementOrder(comp.order, baseParts) }
                : comp
        );

        const orderMap = buildOrderMap(shifted);
        const updated = shifted.map(({ _orig, ...comp }) =>
            applyOrderMapToComponent(comp, orderMap)
        );

        // Refs on the new component may target orders that were shifted by this insert
        updated.push(applyOrderMapToComponent({ ...newComponent }, orderMap));
        return updated.sort((a, b) => compareOrders(a.order, b.order));
    };

    /**
     * Move a component (and its descendants) to toOrder.
     * - Closes the gap at the source
     * - If toOrder is already taken, shifts that sibling and later ones
     * - If toOrder is a new empty slot (e.g. 02.1.01 under an empty true branch), places exactly there
     * - Remaps input / replace / insert / {[order]} on every component
     */
    const moveAndReorder = (components, fromOrder, toOrder) => {
        if (fromOrder === toOrder) {
            return { components, newOrder: toOrder, orderMap: {} };
        }
        // Cannot move a node into its own descendant path
        if (toOrder.startsWith(`${fromOrder}.`)) {
            return { components, newOrder: fromOrder, orderMap: {} };
        }

        const movingBlock = components
            .filter((comp) => comp.order === fromOrder || comp.order.startsWith(`${fromOrder}.`))
            .map((comp) => ({ ...comp, _orig: comp.order }));

        let rest = components
            .filter((comp) => comp.order !== fromOrder && !comp.order.startsWith(`${fromOrder}.`))
            .map((comp) => ({ ...comp, _orig: comp.order }));

        // 1) Close gap left by removing the block at fromOrder
        rest = rest.map((comp) => {
            const nextOrder = decrementOrderAfterRemoval(comp.order, fromOrder);
            return nextOrder === comp.order ? comp : { ...comp, order: nextOrder };
        });

        // Destination may shift if it was after the removed block on the same parent
        const destOrder = decrementOrderAfterRemoval(toOrder, fromOrder);

        // 2) Make room at destination when the slot is already occupied
        if (rest.some((comp) => comp.order === destOrder)) {
            const toParts = destOrder.split(".");
            rest = rest.map((comp) =>
                isAffected(comp.order, toParts)
                    ? { ...comp, order: incrementOrder(comp.order, toParts) }
                    : comp
            );
        }

        // 3) Remap moving block under destOrder
        const remappedBlock = movingBlock.map((comp) => {
            const relativePath = comp._orig.substring(fromOrder.length);
            return { ...comp, order: `${destOrder}${relativePath}` };
        });

        const orderMap = buildOrderMap([...rest, ...remappedBlock]);
        const result = [...rest, ...remappedBlock]
            .map(({ _orig, ...comp }) => applyOrderMapToComponent(comp, orderMap))
            .sort((a, b) => compareOrders(a.order, b.order));

        return { components: result, newOrder: destOrder, orderMap };
    };

    // Select
    const getConditional = () =>
        flowComponent.filter((p) => p.type_component === "decision").map((p) => p.order);

    const getItemsWithChildrenExceptParent = (arr, x) => {
        const withChildren = arr.filter((item) =>
            arr.some((other) => other !== item && other.startsWith(item + "."))
        );
        const parent = x.includes(".") ? x.substring(0, x.lastIndexOf(".")) : null;
        return withChildren.filter((item) => item !== parent);
    };

    const getHigherOrders = (arr, x) => {
        if (!x) return [];

        const chl = getItemsWithChildrenExceptParent(arr, x);
        const conditionals = getConditional();
        const result = chl.filter((item) => !conditionals.includes(item));

        const nOrd = [getNextOrder(flowComponent, "").nextOrder];
        result.forEach((el) => nOrd.push(getNextOrder(flowComponent, el).nextOrder));
        conditionals.forEach((el) => {
            nOrd.push(getNextOrder(flowComponent, el + ".0").nextOrder);
            nOrd.push(getNextOrder(flowComponent, el + ".1").nextOrder);
        });

        const arr2 = [...arr, ...nOrd].filter((v) => compareOrders(v, x, true) > 0);
        return arr2.sort((a, b) => compareOrders(a, b, true));
    };

    const getLowerOrders = (arr, x) => {
        if (!x) return [];
        return arr.filter((v) => v && compareOrders(v, x) < 0).sort((a, b) => compareOrders(a, b));
    };
    //#endregion

    //#region ACTIONS
    const buildComponentDto = (comp) => {
        const dto = {
            type_component: comp.type_component,
            subtype_component: comp.subtype_component,
            order: comp.order,
            body: comp.body && typeof comp.body === "object" ? comp.body : {},
        };

        if (comp.description) dto.description = comp.description;

        if (comp.input && comp.input !== "-1") dto.input = comp.input;

        if (comp.type_component === "parser") {
            if (comp.replace) dto.replace = comp.replace;
            if (comp.insert) dto.insert = comp.insert;
        }

        return dto;
    };

    const handleCreateComponetFlow = (ord) => {
        setitemEditComponentFlow({});
        settypeAction("ADD");
        setOrders(getNextOrder(flowComponent, ord));
        setOpenModal(true);
    };

    const handleAddComponetFlow = (comp, fromOrder) => {
        if (typeAction === "EDIT") {
            const { components: newFlow, newOrder, orderMap } = moveAndReorder(
                flowComponent,
                fromOrder,
                comp.order
            );
            const dat = newFlow.find((F) => F.order === newOrder);
            if (dat) {
                // Form values use pre-move orders; remap them like the rest of the flow
                if (comp?.input && comp.input !== "-1") {
                    const mappedInput = applyOrderMapToValue(comp.input, orderMap);
                    if (mappedInput) dat.input = mappedInput;
                    else delete dat.input;
                } else {
                    delete dat.input;
                }
            }
            setValeComponentFlowInEdition(JSON.stringify({ flow_component: newFlow }));
        } else {
            const newFlow = insertAndReorder(flowComponent, buildComponentDto(comp));
            setValeComponentFlowInEdition(JSON.stringify({ flow_component: newFlow }));
        }
    };

    const handleDeleteSubComponet = (ord) => {
        const newFlow = deleteAndReorder(flowComponent, ord);
        setValeComponentFlowInEdition(JSON.stringify({ flow_component: newFlow }));
    };

    const handleUpdateBodyComponetFlow = (comp, orderEdit) => {
        const newFlow = [...flowComponent];
        const index = newFlow.findIndex((p) => p.order === orderEdit);
        if (index === -1) return;
        newFlow[index].body = comp;
        setValeComponentFlowInEdition(JSON.stringify({ flow_component: newFlow }));
    };

    const handleEditComponet = (data) => {
        setOpenModal(true);
        settypeAction("EDIT");
        setitemEditComponentFlow(data);
        const filtered = [...new Set(flowComponent.filter((fc) => fc.order.startsWith(data.order)).map((i) => i.input))].filter(Boolean);
        const min = filtered.sort(compareOrders)[0];
        const ods = flowComponent.map((e) => e.order);
        const higherOrders = getHigherOrders(ods, min ?? "00").filter((fc) => !fc.startsWith(data.order));
        higherOrders.push(data.order);
        setOrders({ nextOrder: data.order, listOrders: higherOrders });
    };

    const getImputValid = (baseOrder) => {
        const arr = flowComponent.map((e) => e.order);
        const newArr = getLowerOrders(arr, baseOrder);
        newArr.push("-1");
        return newArr;
    };
    //#endregion

    return {
        orders,
        typeAction,
        itemEditComponentFlow,
        handleCreateComponetFlow,
        handleAddComponetFlow,
        handleDeleteSubComponet,
        handleUpdateBodyComponetFlow,
        handleEditComponet,
        getImputValid,
    };
};
